// Text-to-speech hygiene for the chat module and the assistant widget.
//
// Owner report (2026-09-30): when the assistant tried to operate the dashboard,
// the raw tool-call JSON was read aloud.  Tool-call text must NEVER reach TTS —
// only the human reply may.  This helper is the single place that strips the
// things which must never be spoken:
//   • fenced code blocks (``` ... ```)
//   • action-envelope / tool-call JSON ({"actions": [...]})
//   • spoken/show markers ([[say]] / [[/say]] / [[show:...]])
//   • markdown syntax noise that reads badly aloud.

const FENCE_RE = /```[\s\S]*?```/g;
const MARKER_RE = /\[\[\s*\/?\s*(?:say|show)[^\]]*\]\]/gi;

/** Return true when `text` looks like an action envelope / tool-call payload. */
export function looksLikeToolCall(text: string): boolean {
  const trimmed = (text || '').trim();
  if (!trimmed) return false;
  const unfenced = trimmed.replace(/^```(?:json)?\s*/i, '').replace(/```$/, '').trim();
  if (!unfenced.startsWith('{')) return false;
  return /"actions"\s*:/.test(unfenced);
}

/**
 * Strip tool-call JSON / markers / fences from assistant text before speaking.
 * If the whole value is a tool call, only its `reply` field (if any) survives.
 */
export function sanitizeForSpeech(text: string): string {
  let value = (text || '').replace(FENCE_RE, ' ').replace(MARKER_RE, ' ');
  const trimmed = value.trim();
  if (trimmed.startsWith('{')) {
    try {
      const parsed = JSON.parse(trimmed) as { actions?: unknown; reply?: unknown };
      if (parsed && Array.isArray(parsed.actions)) {
        value = typeof parsed.reply === 'string' ? parsed.reply : '';
      }
    } catch {
      // Not valid JSON; fall through to the regex clean-up below.
      if (looksLikeToolCall(trimmed)) value = '';
    }
  }
  return value
    .replace(/[*_#>`~[\]]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}
